<template>
  <MovimentCheckLayout
      title="Retorno"
      titleIcon="fa-solid fa-clipboard-check"
      :loading="loading"
      :totalItems="content.items.length"
      :successMoviment="successMoviment"
      loadingText="Aguarde, estamos preparando o retorno..."
      qtyColumnLabel="Retorno"
  >
    <template #success>
      <SuccessMoviment v-if="successMoviment" :rentId="rentId" :movimentId="movimentId"
                       msgSuccess="Retorno processado com sucesso!"/>
      <AddOrRemoveMaintenance :rentId="rentId" v-show="successMoviment"/>
    </template>

    <template #periods>
      <div class="mcl-period-pill">
        <span class="mcl-period-label">Locação</span>
        <span class="mcl-period-value">{{ content.periodRentCurrent }}</span>
      </div>
      <div class="mcl-period-pill mcl-period-pill--final">
        <span class="mcl-period-label">Retorno</span>
        <span class="mcl-period-value">{{ content.periodRentReturn }}</span>
        <button class="mcl-btn-edit" @click="confirmReturn">
          <i class="fa-solid fa-pen-to-square"></i> Editar
        </button>
      </div>
    </template>

    <template #recalculate>
      <div v-if="(content.isReturnBefore || content.isReturnAfter) && hasRule('Rental')" class="mcl-recalculate">
        <Molded>
          <b-row>
            <b-col xs="12" sm="12" md="12" lg="12" xl="12">
              <RadioGroup title="Recalcular Valor do Produto?" field="periodSubtractRecalculate"
                          :changed="recalculate"
                          :options="[{ text: 'Sim', value: true }, { text: 'Não', value: false }]"
                          v-if="content.isReturnBefore" v-model="periodSubtractRecalculate"/>
              <RadioGroup title="Recalcular Valor do Produto?" field="periodAddRecurrent"
                          :changed="recalculate"
                          :options="[{ text: 'Sim', value: true }, { text: 'Não', value: false }]"
                          v-if="content.isReturnAfter" v-model="periodAddRecurrent"/>
            </b-col>
          </b-row>
        </Molded>
      </div>
    </template>

    <template #extra-th-end>
      <th v-if="hasRule('Maintenance')" class="mcl-th-center">Manutenção</th>
    </template>

    <template #rows>
      <tbody v-for="item in content.items" :key="item.productId || item.productFullName">
      <tr class="mcl-tr" :class="{ 'mcl-tr--maintenance': item.productMaintenance }">
        <td class="mcl-td-product">{{ item.productFullName }}</td>
        <td>
          <div class="mcl-td-desc">
            <InputTextEdit v-model="item.description"/>
          </div>
        </td>
        <td class="mcl-th-center">
          <span class="mcl-qty-badge mcl-qty-badge--rent">{{ item.totalRent }}</span>
        </td>
        <td class="mcl-th-center">
          <span class="mcl-qty-badge mcl-qty-badge--return">{{ item.totalSelected }}</span>
        </td>
        <td class="mcl-th-center">
            <span v-if="!item.isValid" class="mcl-status mcl-status--danger">
              <i class="fa-solid fa-do-not-enter"></i>
            </span>
          <span v-else-if="item.totalSelected < item.totalRent">
                      <template v-if="item.typeProduct != 5">
                        <span class="mcl-status mcl-status--warning">
                          <i class="fa-solid fa-triangle-exclamation"></i>
                        </span>
                      </template>
              <template v-else>
                <span v-if="item.totalSelected == 0" class="mcl-status mcl-status--danger">
                  <i class="fa-solid fa-do-not-enter"></i>
                </span>
                <CheckboxSimple v-else title="Finalizar Retorno" v-model="item.doneMaterial"/>
              </template>
            </span>
          <span v-else class="mcl-status mcl-status--success">
              <i class="fa-solid fa-circle-check"></i>
            </span>
        </td>
        <td v-if="hasRule('Maintenance')" class="mcl-th-center">
          <label class="maint-toggle"
                 :title="item.productMaintenance ? 'Produto irá para manutenção' : 'Marcar para manutenção'">
            <input type="checkbox" class="maint-toggle-input" v-model="item.productMaintenance"
                   @click="addOrRemoveMaintenance(item)">
            <span class="maint-toggle-track">
                <span class="maint-toggle-thumb"></span>
              </span>
          </label>
        </td>
      </tr>
      <tr v-if="item.patrimony.length > 0" class="mcl-tr-patrimony">
        <td colspan="100" class="mcl-td-patrimony"
            :class="{ 'mcl-td-patrimony--maintenance': item.productMaintenance }">
          <div v-for="patrimony in sortedPatrimony(item.patrimony)" :key="patrimony.barCode" class="mcl-patrimony-row">
            <PatrimonyBadge
                :barCode="patrimony.barCode"
                :showCheckbox="hasRule('Maintenance') && item.productMaintenance"
                :checked="patrimony.maintenanceSelected"
                @change="onPatrimonyChange(patrimony, item, $event)"
            />
          </div>
          <Alert
              v-if="hasRule('Maintenance') && item.productMaintenance && item.patrimony.length > 0 && !item.patrimony.some(p => p.maintenanceSelected)"
              type="danger"
              style="margin-top: 8px;">
            <span>Selecione ao menos um patrimônio para enviar para manutenção.</span>
          </Alert>
        </td>
      </tr>
      </tbody>
    </template>

    <template #alerts>
      <Alert type="danger" v-if="isStockNegative" style="margin-top:14px;">
        <span>Não é permitido saída de estoque negativo. Verifique e ajuste os produtos em falta.</span>
      </Alert>
      <Alert type="danger" v-if="generateError" style="margin-top:14px;">
        <span>Ocorreu um erro ao processar o retorno. Tente novamente ou entre em contato com o suporte.</span>
      </Alert>
    </template>

    <template #actions>
      <slot></slot>
      <Button _key="btnGenerateOutput" type="success" :disabled="isStockNegative || isInvalid"
              title="Gerar Retorno" classIcon="fa-solid fa-circle-check" size="large" :clicked="generate"/>
    </template>

    <template #modals>
      <Modal title="Tem certeza que deseja descartar?" :width="500" v-show="showModal('discard')">
        <Confirmation :isModal="false" type="danger" :confirmed="discard">
          <Alert type="warning">
            <span>Não será gerado nenhum período adicional na locação. Essa operação é irreversível.</span>
          </Alert>
        </Confirmation>
      </Modal>

      <Modal title="Data Fim" :width="500" v-show="showModal('editReturn')">
        <DateTime title="Fim" field="endDateHourReturn" format="DD/MM/YYYY HH:mm" type="dateTime"
                  v-model="endDateHourReturn"/>
        <div class="text-center">
          <Button _key="btnEndDateHourReturn" type="primary" title="Aplicar"
                  size="medium" :clicked="editReturn"/>
        </div>
      </Modal>

      <Modal title="Alerta" :width="900" :closeOnEsc="false" :closeButton="false"
             v-show="showModal('isReturnBeforeAfter')">
        <Warning title="ATENÇÃO" :periodRent="content.periodRentCurrent" :periodRentReturn="content.periodRentReturn"
                 @confirm="confirm">
          <span class="title">
            A Locação está sendo retornada
            <b>
              <DisplayCalculatePeriod :fontSize="16" :periodRent="{
                year: content.year, month: content.month, day: content.day,
                hour: content.hour, minute: content.minute,
              }"/>
            </b>
          </span>
          <span class="title" v-if="content.isReturnBefore"><b> ANTES </b> da data prevista,</span>
          <span class="title" v-else><b> APÓS </b> a data fim,</span>
          <span class="title">selecione o período correto para continuar!</span>
        </Warning>
      </Modal>
    </template>
  </MovimentCheckLayout>
</template>

<script>
import MovimentCheckLayout from "../shared/MovimentCheckLayout.vue";
import AddOrRemoveMaintenance from "../AddOrRemoveMaintenance.vue";
import Molded from "@nixweb/nixloc-ui/src/component/layout/Molded";
import Button from "@nixweb/nixloc-ui/src/component/forms/Button";
import Modal from "@nixweb/nixloc-ui/src/component/forms/Modal";
import Alert from "@nixweb/nixloc-ui/src/component/layout/Alert";
import InputTextEdit from "@nixweb/nixloc-ui/src/component/forms/InputTextEdit.vue";
import Confirmation from "@nixweb/nixloc-ui/src/component/shared/Confirmation.vue";
import DateTime from "@nixweb/nixloc-ui/src/component/forms/DateTime";
import RadioGroup from "@nixweb/nixloc-ui/src/component/forms/RadioGroup.vue";
import CheckboxSimple from "@nixweb/nixloc-ui/src/component/forms/CheckboxSimple.vue";
import DisplayCalculatePeriod from "@nixweb/nixloc-ui/src/component/rental/DisplayCalculatePeriod";
import Warning from "../Warning.vue";
import SuccessMoviment from "../SuccessMoviment.vue";
import PatrimonyBadge from "../shared/PatrimonyBadge.vue";

import {mapActions, mapGetters, mapMutations, mapState} from "vuex";

export default {
  name: "CheckReturn",
  components: {
    MovimentCheckLayout,
    Molded,
    Button,
    Modal,
    Alert,
    CheckboxSimple,
    InputTextEdit,
    DisplayCalculatePeriod,
    SuccessMoviment,
    Confirmation,
    DateTime,
    RadioGroup,
    AddOrRemoveMaintenance,
    Warning,
    PatrimonyBadge,
  },
  props: {
    rentId: String,
  },
  data() {
    return {
      urlCheck: "/api/v1/moviment/generate-return/check-return",
      urlGenerate: "/api/v1/moviment/generate-return/generate",
      content: {items: []},
      endDateHourCurrent: "",
      endDateHourReturn: "",
      loading: true,
      successMoviment: false,
      movimentId: "",
      isInvalid: true,
      periodSubtractRecalculate: false,
      periodAddRecurrent: false,
      generateError: false,
    };
  },
  mounted() {
    this.periodSubtractRecalculate = this.userLogged.parameterRental.periodSubtractRecalculate;
    this.periodAddRecurrent = this.userLogged.parameterRental.periodAddRecurrent;
    this.checkReturn();
  },
  computed: {
    ...mapGetters("generic", ["showModal"]),
    ...mapState("user", ["userLogged"]),
    ...mapGetters("user", ["hasRule"]),
    ...mapGetters("generateMoviment", ["productSelectedByPeriod"]),
    ...mapState("generateMoviment", ["periodRent", "productRent"]),
    isStockNegative() {
      return false;
    },
  },
  methods: {
    ...mapActions("generic", ["postApi"]),
    ...mapMutations("generic", ["openModal", "removeLoading", "hideModal", "addEvent"]),
    ...mapMutations("generateMoviment", ["cleanProductSelected"]),
    checkReturn() {
      this.loading = true;
      let obj = {
        endDateHour: this.endDateHourCurrent,
        periodRent: this.periodRent.fullPeriod,
        productRent: this.productRent,
        productSelected: this.productSelectedByPeriod,
        isReturnBefore: this.content.isReturnBefore,
        isReturnAfter: this.content.isReturnAfter,
        periodAddRecurrent: this.periodAddRecurrent,
        periodSubtractRecalculate: this.periodSubtractRecalculate,
        periodRenewedRecalculate: this.periodRenewedRecalculate,
      };
      let params = {url: this.urlCheck, obj: obj, notNotifyToast: true};
      this.postApi(params).then((response) => {
        if (response.success) {
          this.content = response.content;
          this.content.items.forEach(item => {
            item.patrimony.forEach(p => {
              this.$set(p, 'maintenanceSelected', false);
            });
          });
          this.endDateHourReturn = this.content.endDateHourReturn;
          this.isInvalid = false;
          if (this.content.isReturnBefore || this.content.isReturnAfter) {
            this.openModal("isReturnBeforeAfter");
          }
        } else {
          this.isInvalid = true;
        }
        this.loading = false;
      });
    },
    confirmDiscard() {
      this.openModal("discard");
    },
    confirm(option) {
      if (option == "periodRent") this.discard();
      if (option == "newPeriod") this.hideModal();
    },
    discard() {
      this.hideModal();
      this.removeLoading(["confirm"]);
      this.endDateHourCurrent = this.content.endDateHourCurrent;
      this.checkReturn();
    },
    confirmReturn() {
      this.openModal("editReturn");
    },
    editReturn() {
      this.hideModal();
      this.removeLoading(["btnEndDateHourReturn"]);
      this.endDateHourCurrent = this.endDateHourReturn;
      this.checkReturn();
    },
    generate() {
      this.loading = true;
      this.generateError = false;
      this.content.rentId = this.rentId;
      let recalculate = {
        periodAddRecurrent: this.periodAddRecurrent,
        periodSubtractRecalculate: this.periodSubtractRecalculate,
      };
      let params = {
        url: this.urlGenerate,
        obj: {...this.content, ...recalculate},
        notNotifyToast: false,
      };
      this.postApi(params).then((response) => {
        if (response.success) {
          this.movimentId = response.content;
          let self = this;
          setTimeout(function () {
            self.successMoviment = true;
            self.loading = false;
            self.cleanProductSelected();
            self.removeLoading(["btnGenerateOutput"]);
          }, 300);
        } else {
          this.loading = false;
          this.generateError = true;
          this.removeLoading(["btnGenerateOutput"]);
        }
      });
    },
    recalculate() {
    },
    sortedPatrimony(patrimonies) {
      return [...patrimonies].sort((a, b) => (a.barCode || '').localeCompare(b.barCode || ''));
    },
    hideModalIsReturnBeforeAfter() {
      this.removeLoading(["btnIsReturnBeforeAfter"]);
      this.hideModal("isReturnBeforeAfter");
    },
    addOrRemoveMaintenance(params) {
      this.addEvent({name: "addOrRemoveMaintenance", data: params});
    },
    onPatrimonyChange(patrimony, item, checked) {
      this.$set(patrimony, 'maintenanceSelected', checked);
      this.addOrRemoveMaintenance({...patrimony, ...item});
    },
  },
  watch: {
    event: {
      handler(event) {
        if (event.name == "timeLineAvailability") {
          this.openModal("timeLineAvailability");
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.title {
  font-size: 15px;
}

.maint-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  margin: 0;
}

.maint-toggle-input {
  display: none;
}

.maint-toggle-track {
  position: relative;
  width: 38px;
  height: 22px;
  background: #d1d5db;
  border-radius: 999px;
  transition: background 0.25s;
  flex-shrink: 0;
}

.maint-toggle-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  transition: transform 0.25s;
}

.maint-toggle-input:checked ~ .maint-toggle-track {
  background: #f59e0b;
}

.maint-toggle-input:checked ~ .maint-toggle-track .maint-toggle-thumb {
  transform: translateX(16px);
}

.maint-toggle-icon {
  font-size: 13px;
  color: #9ca3af;
  transition: color 0.25s;
}

.maint-toggle-input:checked ~ .maint-toggle-icon {
  color: #f59e0b;
}

.mcl-tr--maintenance {
  background-color: #fffbeb !important;
  border-left: 3px solid #f59e0b;
  transition: background-color 0.25s;
}

.mcl-tr--maintenance td {
  color: #F59E0D;
}

.mcl-td-patrimony--maintenance {
  background-color: #fef3c7 !important;
  border-left: 3px solid #fde68a;
}

.mcl-patrimony-alert {
  margin: 10px 10px 4px 10px;
  padding: 8px 12px;
  background-color: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 6px;
  color: #c2410c;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>

<style>
.dark-theme .mcl-tr--maintenance {
  background-color: #292013 !important;
  border-left-color: #f59e0b !important;
}

.dark-theme .mcl-tr--maintenance td {
  color: #fde68a !important;
}

.dark-theme .mcl-td-patrimony--maintenance {
  background-color: #1f1a0d !important;
  border-left-color: #78500a !important;
}

.dark-theme .mcl-patrimony-alert {
  background-color: #2d1a0a !important;
  border-color: #92400e !important;
  color: #fdba74 !important;
}
</style>

